import { categories, videos, articles, siteInfo } from '@/mocks/data';
import { Link } from 'react-router-dom';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';

const categoryIcons: Record<string, string> = {
  dianying: 'ri-film-line',
  dianshiju: 'ri-tv-line',
  zongyi: 'ri-emotion-laugh-line',
  dongman: 'ri-ghost-smile-line',
  jilupian: 'ri-global-line',
  tiyu: 'ri-basketball-line',
  yinyue: 'ri-music-line',
  youxi: 'ri-gamepad-line',
};

export default function HomePage() {
  const hotVideos = videos.filter((v) => v.isHot).slice(0, 8);
  const newVideos = videos.filter((v) => v.isNew).slice(0, 8);
  const featuredArticles = articles.slice(0, 4);

  return (
    <>
      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '樱桃视频',
            url: 'https://www.yingtao-video.com/',
            description: siteInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.yingtao-video.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '樱桃视频',
            url: 'https://www.yingtao-video.com/',
            description: siteInfo.description,
            foundingDate: '2020',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'contact@yingtaovideo.com',
              contactType: 'customer service',
            },
          }),
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20abstract%20background%20with%20flowing%20crimson%20and%20deep%20black%20gradient%20waves%2C%20subtle%20film%20grain%20texture%2C%20elegant%20curves%2C%20luxurious%20atmosphere%2C%20movie%20theater%20vibe%2C%20dramatic%20lighting%2C%20no%20text&width=1920&height=900&seq=hero-bg-cherry-video&orientation=landscape"
            alt="樱桃视频 - 免费高清影视平台"
            title="樱桃视频 - 免费高清在线观看"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/20 border border-primary-500/30 mb-6">
              <i className="ri-sparkling-2-line text-primary-400 text-sm"></i>
              <span className="text-xs text-primary-300 font-medium whitespace-nowrap">每日更新 1000+ 高清资源</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-50 font-heading leading-tight">
              免费高清影视
              <br />
              <span className="text-primary-400">流畅不卡顿</span> 即刻观影
            </h1>
            <p className="mt-5 text-sm md:text-base text-foreground-300 max-w-2xl mx-auto leading-relaxed">
              樱桃视频聚合海量优质影视资源，涵盖电影、电视剧、综艺、动漫、纪录片等多种类型。
              支持高清画质在线播放，无需会员，完全免费，给你最流畅的观影体验。
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="flex items-center bg-background-100/80 backdrop-blur-sm rounded-full px-5 py-3 gap-3 w-full sm:w-96 border border-background-300/40">
                <i className="ri-search-line text-foreground-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="搜索你喜欢的电影、电视剧..."
                  className="bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none flex-1 border-none"
                />
                <button className="px-5 py-1.5 bg-primary-500 hover:bg-primary-600 text-foreground-50 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap font-medium">
                  搜索
                </button>
              </div>
            </div>

            <div className="mt-10 flex items-center justify-center gap-8 md:gap-12 flex-wrap">
              {[
                { value: siteInfo.users, label: '累计用户' },
                { value: siteInfo.videos, label: '影视资源' },
                { value: siteInfo.dailyActive, label: '日活用户' },
                { value: '99.9%', label: '播放流畅率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <i className="ri-arrow-down-line text-foreground-400 text-2xl"></i>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-50 font-heading">全部分类</h2>
            <Link to="/category/dianying" className="text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 border border-background-200/40 hover:border-primary-500/30 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-500/10 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${categoryIcons[cat.id] || 'ri-film-line'} text-primary-400 text-xl md:text-2xl`}></i>
                </div>
                <span className="text-xs md:text-sm text-foreground-300 group-hover:text-foreground-50 transition-colors whitespace-nowrap font-medium">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Videos */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 rounded-full bg-primary-500"></div>
            <h2 className="text-lg md:text-xl font-bold text-foreground-50 font-heading">热门推荐</h2>
            <div className="flex items-center gap-1 ml-2">
              <i className="ri-fire-fill text-primary-400 text-lg"></i>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {hotVideos.map((video) => (
              <Link key={video.id} to={`/article/${video.id}`} className="block">
                <VideoCard video={video} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Videos */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 rounded-full bg-accent-500"></div>
            <h2 className="text-lg md:text-xl font-bold text-foreground-50 font-heading">最新上线</h2>
            <div className="flex items-center gap-1 ml-2">
              <i className="ri-flashlight-fill text-accent-400 text-lg"></i>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {newVideos.map((video) => (
              <Link key={video.id} to={`/article/${video.id}`} className="block">
                <VideoCard video={video} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Banner */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-background-200 via-background-100 to-background-200 p-8 md:p-12">
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  为什么选择 <span className="text-primary-400">樱桃视频</span>？
                </h3>
                <p className="text-sm text-foreground-400 mt-2 max-w-md">
                  高清画质 · 流畅不卡顿 · 完全免费 · 海量资源
                </p>
              </div>
              <div className="flex items-center gap-4 md:gap-8 flex-wrap justify-center">
                {[
                  { icon: 'ri-4k-line', label: '超清画质' },
                  { icon: 'ri-speed-up-line', label: '极速加载' },
                  { icon: 'ri-shield-check-line', label: '安全无广告' },
                  { icon: 'ri-device-line', label: '多端支持' },
                ].map((item) => (
                  <div key={item.label} className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/15">
                      <i className={`${item.icon} text-primary-400 text-xl`}></i>
                    </div>
                    <span className="text-xs text-foreground-300 whitespace-nowrap">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-primary-500"></div>
              <h2 className="text-lg md:text-xl font-bold text-foreground-50 font-heading">影视资讯</h2>
            </div>
            <Link to="/category/dianying" className="text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
              更多资讯 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
            准备好开始观影了吗？
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-xl mx-auto">
            海量高清影视资源等你发现，立即加入樱桃视频，享受流畅不卡顿的免费观影体验！
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/dianying"
              className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-foreground-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              开始免费观影
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-background-200 hover:bg-background-300 text-foreground-200 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}